import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Grid,
  SimpleGrid,
  Text,
  Spinner,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Collapse,
  Button,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import {
  CaretRight,
  Warning,
  WarningCircle,
  CheckCircle,
  Gauge,
  ChartLineUp,
  ArrowSquareOut,
} from 'phosphor-react';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  Legend,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { useAuditoriaInteligente, IPeriodoParams } from '../../../../hooks/useAuditoriaInteligente';
import { num } from '../format';
import {
  IAuditoriaEmpresa,
  IAuditoriaItem,
  IAuditoriaResumo,
  IAuditoriaPorRegra,
  AuditoriaArea,
} from '../types';
import { KpiCard } from '../components/KpiCard';
import { InfoBloco, FILTRO } from '../components/InfoPopover';
import { StatusBadge } from '../components/StatusBadge';
import { EmptyState } from '../components/EmptyState';
import { UI } from '../ui';
import { splitLados, fmtLado, valorDiferenca } from './_shared/cruzamento';

/** Área do apontamento → submódulo-fonte para o botão "Abrir". */
const AREA_TO_SCREEN: Record<string, { seg: string; label: string }> = {
  fc: { seg: 'integracao', label: 'Integração Fiscal-Contábil' },
  ff: { seg: 'pessoal', label: 'Pessoal' },
  fo: { seg: 'obrigacoes', label: 'Obrigações Acessórias' },
  nf: { seg: 'notas', label: 'Notas Fiscais' },
  sn: { seg: 'simples', label: 'Simples Nacional' },
};

const AREA_LABEL: Record<string, string> = {
  fc: 'Fiscal × Contábil',
  ff: 'Fiscal × Folha',
  fo: 'Folha × Obrigações',
  nf: 'Notas × Fiscal',
  sn: 'Simples × Notas',
};

interface PorAreaAgg {
  area: AuditoriaArea;
  verificacoes: number;
  divergencias: number;
  alertas: number;
}

interface AuditoriasScreenProps {
  empresa: IAuditoriaEmpresa | null;
  periodo: IPeriodoParams;
  /** Área inicial (deep-link de Cruzamentos): pré-filtra o painel por esta área. */
  areaInicial?: AuditoriaArea;
  /** Linha a abrir já expandida (foco): key = `${area}-${regra}-${periodo}`. */
  focusKey?: string;
  /** Hyperlink: abre o submódulo-fonte do apontamento. */
  onScreen: (screen: string) => void;
}

const AREAS: { value: AuditoriaArea | ''; label: string }[] = [
  { value: '', label: 'Todas as áreas' },
  { value: 'fc', label: 'Fiscal × Contábil' },
  { value: 'ff', label: 'Fiscal × Folha' },
  { value: 'fo', label: 'Folha × Obrigações' },
  { value: 'nf', label: 'Notas × Fiscal' },
  { value: 'sn', label: 'Simples × Notas' },
];

const STATUS_FILTROS: { value: string; label: string }[] = [
  { value: '', label: 'Pendências (Div. + Alertas)' },
  { value: 'DIVERGENCIA', label: 'Divergências' },
  { value: 'ALERTA', label: 'Alertas' },
  { value: 'OK', label: 'Conferências OK' },
];

export function AuditoriasScreen({
  empresa,
  periodo,
  areaInicial,
  focusKey,
  onScreen,
}: AuditoriasScreenProps) {
  const { getAuditorias, getAuditoriasResumo } = useAuditoriaInteligente();
  const [status, setStatus] = useState('');
  const [area, setArea] = useState<AuditoriaArea | ''>(areaInicial ?? '');
  const [open, setOpen] = useState<string | null>(null);

  // Re-navegar de Cruzamentos (mesma tela, nova área) deve atualizar o filtro.
  useEffect(() => {
    setArea(areaInicial ?? '');
  }, [areaInicial]);

  // Deep-link com foco (de Cruzamentos): abre a linha do apontamento expandida.
  useEffect(() => {
    if (focusKey) setOpen(focusKey);
  }, [focusKey]);

  const { data: lista, isFetching } = useQuery<IAuditoriaItem[]>({
    queryKey: [
      'ai-auditorias',
      { id: empresa?.id, status: status || undefined, area: area || undefined, ...periodo },
    ],
    queryFn: getAuditorias,
    enabled: !!empresa,
  });

  const { data: resumo } = useQuery<IAuditoriaResumo>({
    queryKey: ['ai-auditorias-resumo', { id: empresa?.id, ...periodo }],
    queryFn: getAuditoriasResumo,
    enabled: !!empresa,
  });

  // Apontamentos por área (agregado de por_regra) — alimenta o painel + drills.
  const porArea = useMemo(() => {
    const map = new Map<AuditoriaArea, PorAreaAgg>();
    (resumo?.por_regra ?? []).forEach(r => {
      const a = map.get(r.area) ?? { area: r.area, verificacoes: 0, divergencias: 0, alertas: 0 };
      a.verificacoes += num(r.verificacoes);
      a.divergencias += num(r.divergencias);
      a.alertas += num(r.alertas);
      map.set(r.area, a);
    });
    return Array.from(map.values()).sort((x, y) => y.divergencias - x.divergencias);
  }, [resumo]);
  const maxArea = Math.max(...porArea.map(a => a.divergencias + a.alertas), 1);

  // Top regras com mais divergências.
  const topRegras = useMemo(
    () =>
      [...(resumo?.por_regra ?? [])]
        .filter(r => num(r.divergencias) > 0)
        .sort((a, b) => num(b.divergencias) - num(a.divergencias))
        .slice(0, 6),
    [resumo],
  );

  if (!empresa) {
    return (
      <EmptyState
        title="Selecione uma empresa"
        message="Use a busca no topo (ID, CNPJ ou Razão Social) para abrir o painel de auditorias."
        icon={Warning}
      />
    );
  }

  const c = resumo?.contagem;
  const itens = lista ?? [];

  return (
    <Box>
      {/* Contadores */}
      <Grid templateColumns={{ base: '1fr 1fr', md: 'repeat(5, 1fr)' }} gap={4} mb={5}>
        <KpiCard
          label="Verificações"
          value={c?.total ?? 0}
          icon={ChartLineUp}
          iconColor={UI.brand}
          iconBg={UI.infoSoft}
          info={
            <InfoBloco
              oque="Total de conferências executadas pelo motor de regras de auditoria para esta empresa (soma de divergências, alertas e OKs)."
              dados="CTE todas_regras (motor on-the-fly) · COUNT(*) sobre todas as regras de cruzamento; guard de regime exclui regras 'SN-' para não-optantes."
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Divergências"
          value={c?.red ?? 0}
          icon={WarningCircle}
          iconColor={UI.neg}
          iconBg={UI.negSoft}
          info={
            <InfoBloco
              oque="Conferências em que os dois lados do cruzamento não batem — exigem correção. É o sinal crítico que puxa a classificação para ATENÇÃO/CRÍTICO."
              dados="CTE todas_regras · COUNT(*) FILTER (status = 'DIVERGENCIA')."
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Alertas"
          value={c?.amber ?? 0}
          icon={Warning}
          iconColor={UI.warn}
          iconBg={UI.warnSoft}
          info={
            <InfoBloco
              oque="Conferências com indício de problema dentro da tolerância — não são erro confirmado, mas merecem revisão manual."
              dados="CTE todas_regras · COUNT(*) FILTER (status = 'ALERTA')."
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="OK"
          value={c?.green ?? 0}
          icon={CheckCircle}
          iconColor={UI.pos}
          iconBg={UI.posSoft}
          info={
            <InfoBloco
              oque="Conferências que bateram — os dois lados do cruzamento são consistentes. Quanto maior em relação ao total, melhor o Score."
              dados="CTE todas_regras · COUNT(*) FILTER (status = 'OK')."
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Score"
          value={c ? `${c.score}` : '—'}
          sub={c?.classificacao}
          icon={Gauge}
          iconColor="#8B5CF6"
          iconBg="#F0EAFB"
          onClick={() => onScreen('dossie')}
          info={
            <InfoBloco
              oque="Percentual de conferências OK sobre o total (0–100). A classificação (OK/REVISAR/ATENÇÃO/CRÍTICO) deriva da quantidade de divergências e alertas. Clique para abrir o Dossiê."
              dados="CTE todas_regras · ROUND(COUNT(*) FILTER (status='OK') / NULLIF(COUNT(*),0) * 100, 0); classificacao por faixas de divergências/alertas."
              filtro={FILTRO.empresa}
            />
          }
        />
      </Grid>

      {/* Evolução do score */}
      {resumo?.evolucao && resumo.evolucao.length > 0 && (
        <Box
          bg={UI.glass}
          border="1px solid"
          borderColor={UI.glassBorder}
          borderRadius="16px"
          backdropFilter={UI.glassBlur}
          boxShadow={UI.glassShadow}
          p={5}
          mb={5}
        >
          <Text fontSize="13px" fontWeight={700} mb={3} color={UI.ink}>
            Evolução (divergências × alertas × score por mês)
          </Text>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={resumo.evolucao}>
              <defs>
                <linearGradient id="aiDiv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={UI.neg} stopOpacity={0.25} />
                  <stop offset="100%" stopColor={UI.neg} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="aiAlert" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={UI.warn} stopOpacity={0.25} />
                  <stop offset="100%" stopColor={UI.warn} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={UI.border} />
              <XAxis dataKey="mes" fontSize={11} stroke={UI.muted} />
              <YAxis yAxisId="l" fontSize={11} stroke={UI.muted} allowDecimals={false} />
              <YAxis
                yAxisId="r"
                orientation="right"
                domain={[0, 100]}
                fontSize={11}
                stroke={UI.muted}
                tickFormatter={v => `${v}`}
              />
              <Tooltip />
              <Legend />
              <Area
                yAxisId="l"
                type="monotone"
                dataKey="divergencias"
                name="Divergências"
                stroke={UI.neg}
                fill="url(#aiDiv)"
                strokeWidth={2}
              />
              <Area
                yAxisId="l"
                type="monotone"
                dataKey="alertas"
                name="Alertas"
                stroke={UI.warn}
                fill="url(#aiAlert)"
                strokeWidth={2}
              />
              <Line
                yAxisId="r"
                type="monotone"
                dataKey="score"
                name="Score"
                stroke="#8B5CF6"
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      )}

      {/* Apontamentos por área + top regras */}
      {porArea.length > 0 && (
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5} mb={5}>
          <Box
            bg={UI.glass}
            border="1px solid"
            borderColor={UI.glassBorder}
            borderRadius="16px"
            backdropFilter={UI.glassBlur}
            boxShadow={UI.glassShadow}
            p={5}
          >
            <Text fontSize="13px" fontWeight={800} mb={4} color={UI.ink}>
              Apontamentos por área
            </Text>
            {porArea.map(a => (
              <Flex
                key={a.area}
                align="center"
                gap={3}
                mb={2.5}
                cursor="pointer"
                onClick={() => setArea(a.area)}
                title={`Filtrar por ${AREA_LABEL[a.area] ?? a.area}`}
                _hover={{ opacity: 0.85 }}
              >
                <Text
                  w="116px"
                  fontSize="12px"
                  fontWeight={700}
                  color={UI.ink}
                  flexShrink={0}
                  noOfLines={1}
                >
                  {AREA_LABEL[a.area] ?? a.area}
                </Text>
                <Box
                  flex={1}
                  h="9px"
                  borderRadius="full"
                  bg="rgba(15,23,42,0.06)"
                  overflow="hidden"
                  display="flex"
                >
                  <Box h="100%" bg={UI.neg} w={`${(a.divergencias / maxArea) * 100}%`} />
                  <Box h="100%" bg={UI.warn} w={`${(a.alertas / maxArea) * 100}%`} />
                </Box>
                <Text w="84px" textAlign="right" fontSize="12px" fontWeight={700} flexShrink={0}>
                  <Text as="span" color={UI.neg}>
                    {a.divergencias}
                  </Text>{' '}
                  ·{' '}
                  <Text as="span" color={UI.warn}>
                    {a.alertas}
                  </Text>
                </Text>
                <Box color={UI.faint} flexShrink={0}>
                  <ArrowSquareOut size={13} weight="bold" />
                </Box>
              </Flex>
            ))}
            <Text fontSize="10.5px" color={UI.faint} mt={2}>
              <Text as="span" color={UI.neg}>
                ■
              </Text>{' '}
              divergências{' · '}
              <Text as="span" color={UI.warn}>
                ■
              </Text>{' '}
              alertas
            </Text>
          </Box>

          <Box
            bg="white"
            border="1px solid"
            borderColor={UI.border}
            borderRadius="16px"
            overflow="hidden"
          >
            <Box px={5} pt={4} pb={2}>
              <Text fontSize="13px" fontWeight={800} color={UI.ink}>
                Top regras com divergências
              </Text>
            </Box>
            {topRegras.length === 0 ? (
              <Box px={5} pb={5}>
                <Text fontSize="13px" color={UI.pos}>
                  Nenhuma regra com divergência. ✓
                </Text>
              </Box>
            ) : (
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead bg={UI.rowHover}>
                    <Tr>
                      <Th>Regra</Th>
                      <Th>Área</Th>
                      <Th isNumeric>Divergências</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {topRegras.map((r: IAuditoriaPorRegra) => (
                      <Tr
                        key={`${r.area}-${r.regra}`}
                        cursor="pointer"
                        _hover={{ bg: UI.rowHover }}
                        onClick={() => setArea(r.area)}
                        title={r.descricao}
                      >
                        <Td fontWeight={700} maxW="220px" whiteSpace="normal">
                          {r.regra}
                        </Td>
                        <Td color={UI.muted}>{AREA_LABEL[r.area] ?? r.area}</Td>
                        <Td isNumeric fontWeight={800} color={UI.neg}>
                          {num(r.divergencias)}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </SimpleGrid>
      )}

      {/* Filtros */}
      <Flex gap={3} align="flex-end" wrap="wrap" mb={4}>
        <Box>
          <Text
            fontSize="10.5px"
            fontWeight={700}
            color={UI.muted}
            mb={1}
            textTransform="uppercase"
          >
            Status
          </Text>
          <Select size="sm" w="240px" value={status} onChange={e => setStatus(e.target.value)}>
            {STATUS_FILTROS.map(s => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
        </Box>
        <Box>
          <Text
            fontSize="10.5px"
            fontWeight={700}
            color={UI.muted}
            mb={1}
            textTransform="uppercase"
          >
            Área
          </Text>
          <Select
            size="sm"
            w="200px"
            value={area}
            onChange={e => setArea(e.target.value as AuditoriaArea | '')}
          >
            {AREAS.map(a => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </Select>
        </Box>
      </Flex>

      {/* Lista expansível */}
      <Box
        bg="white"
        border="1px solid"
        borderColor={UI.border}
        borderRadius="16px"
        overflow="hidden"
      >
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead bg={UI.rowHover}>
              <Tr>
                <Th w="34px" />
                <Th>Regra</Th>
                <Th>Descrição</Th>
                <Th>Período</Th>
                <Th>Status</Th>
                <Th isNumeric>Diferença</Th>
              </Tr>
            </Thead>
            <Tbody>
              {isFetching && (
                <Tr>
                  <Td colSpan={6} textAlign="center" py={8}>
                    <Spinner />
                  </Td>
                </Tr>
              )}
              {!isFetching &&
                itens.map(item => {
                  const key = `${item.area}-${item.regra}-${item.periodo}`;
                  const isOpen = open === key;
                  const { labelA, labelB } = splitLados(item.descricao);
                  return (
                    <React.Fragment key={key}>
                      <Tr
                        cursor="pointer"
                        _hover={{ bg: UI.rowHover }}
                        bg={isOpen ? '#F0F7FE' : undefined}
                        onClick={() => setOpen(isOpen ? null : key)}
                      >
                        <Td color={UI.muted}>
                          <Box
                            as="span"
                            display="inline-block"
                            transition="transform .15s"
                            transform={isOpen ? 'rotate(90deg)' : undefined}
                          >
                            <CaretRight size={14} />
                          </Box>
                        </Td>
                        <Td fontWeight={700}>{item.regra}</Td>
                        <Td maxW="360px" whiteSpace="normal">
                          {item.descricao}
                        </Td>
                        <Td>{item.periodo}</Td>
                        <Td>
                          <StatusBadge status={item.status} badge={item.badge} />
                        </Td>
                        <Td isNumeric fontWeight={700}>
                          {valorDiferenca(item.regra, item.diferenca)}
                        </Td>
                      </Tr>
                      <Tr>
                        <Td colSpan={6} p={0} border="none">
                          <Collapse in={isOpen} animateOpacity>
                            <Box bg="#F7FAFD" px={6} py={4}>
                              <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                                <Box>
                                  <Text fontSize="11px" color={UI.muted} fontWeight={700}>
                                    {labelA.toUpperCase()}
                                  </Text>
                                  <Text fontSize="15px" fontWeight={800}>
                                    {fmtLado(item.regra, item.valor_a)}
                                  </Text>
                                </Box>
                                <Box>
                                  <Text fontSize="11px" color={UI.muted} fontWeight={700}>
                                    {(labelB ?? '—').toUpperCase()}
                                  </Text>
                                  <Text fontSize="15px" fontWeight={800}>
                                    {labelB ? fmtLado(item.regra, item.valor_b) : '—'}
                                  </Text>
                                </Box>
                                <Box>
                                  <Text fontSize="11px" color={UI.muted} fontWeight={700}>
                                    DIFERENÇA
                                  </Text>
                                  <Text
                                    fontSize="15px"
                                    fontWeight={800}
                                    color={num(item.diferenca) !== 0 ? UI.neg : UI.pos}
                                  >
                                    {valorDiferenca(item.regra, item.diferenca)}
                                  </Text>
                                </Box>
                              </Grid>
                              {AREA_TO_SCREEN[item.area] && (
                                <Button
                                  size="sm"
                                  mt={4}
                                  variant="outline"
                                  borderColor={UI.border}
                                  color={UI.brand}
                                  leftIcon={<ArrowSquareOut size={14} weight="bold" />}
                                  _hover={{ bg: UI.infoSoft, borderColor: UI.brand }}
                                  onClick={() => onScreen(AREA_TO_SCREEN[item.area].seg)}
                                >
                                  Abrir {AREA_TO_SCREEN[item.area].label}
                                </Button>
                              )}
                            </Box>
                          </Collapse>
                        </Td>
                      </Tr>
                    </React.Fragment>
                  );
                })}
              {!isFetching && itens.length === 0 && (
                <Tr>
                  <Td colSpan={6} textAlign="center" py={8} color={UI.muted}>
                    Nenhum apontamento para os filtros selecionados.
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
